/**
 * Vista de Candidatos Listos para Contratar
 * Fecha: 16 Oct 2025
 *
 * Muestra la lista de candidatos en estado SELECCIONADO listos para ser contratados
 */
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { hiringService } from '@/features/rh/api/hiring.service';
import { Badge } from '@/shared/components';
import { UserPlus, Search, Users } from "lucide-react";
import { userHasPermission } from '@/shared/lib/roleUtils';
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage';
import { useAuth } from '@/shared/hooks/useAuth';
import { EsqueletoTabla } from "@/shared/components/Esqueleto";
import FilterSelect from '@/shared/components/FilterSelect';
import { formatFechaEnZona } from '@/shared/lib/dateUtils';

interface JobRequest {
  id: number;
  areaId: number;
  areaVacante: string;
  puestoSolicitar: string;
  sede: string;
  jornada: string;
  prioridad: string;
  numeroPlazas: number;
  fechaRequeridaInicio: string;
  habilidadesMinimas: string;
  salarioMinimo?: string;
  salarioMaximo?: string;
  status: string;
  solicitante?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface Candidate {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  age: number;
  experienceYears: number;
  experienceDescription: string;
  cvUrl: string | null;
  status: string;
  jobRequestId: number;
  userId: string;
  createdAt: string;
  updatedAt: string;
  jobRequest?: JobRequest;
}

/**
 * Valores que de verdad existen en la lista, para no ofrecer filtros vacíos.
 *
 * Va fuera del componente: adentro sería una función nueva en cada render y
 * los `useMemo` que la usan la pedirían como dependencia.
 */
const opcionesDe = (
  lista: Candidate[],
  saca: (c: Candidate) => string | undefined,
): string[] =>
  Array.from(
    new Set(lista.map(saca).filter((v): v is string => Boolean(v))),
  ).sort((a, b) => a.localeCompare(b, "es"));

export const CandidatesReadyToHire: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroArea, setFiltroArea] = useState("");
  const [filtroPuesto, setFiltroPuesto] = useState("");
  const [filtroSede, setFiltroSede] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  useEffect(() => {
    loadCandidates();
  }, []);

  const loadCandidates = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await hiringService.getCandidatesReady();
      setCandidates(data);
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al cargar candidatos:", err);
      setError(err.message || "Error al cargar candidatos");
    } finally {
      setIsLoading(false);
    }
  };

  const areas = useMemo(
    () => opcionesDe(candidates, (c) => c.jobRequest?.areaVacante),
    [candidates],
  );
  const puestos = useMemo(
    () => opcionesDe(candidates, (c) => c.jobRequest?.puestoSolicitar),
    [candidates],
  );
  const sedes = useMemo(
    () => opcionesDe(candidates, (c) => c.jobRequest?.sede),
    [candidates],
  );

  /**
   * La lista filtrada se deriva, no se guarda. Antes vivía en su propio estado
   * que un efecto sincronizaba, y con cinco filtros eso es una fuente de
   * desajustes: basta olvidar uno en las dependencias.
   *
   * Las fechas se comparan como texto `YYYY-MM-DD` ya convertido a la zona de
   * la operación: `createdAt` llega en UTC y a partir de las 18:00 cae en el
   * día siguiente, así que cortar la cadena mostraría un día de más.
   */
  const filteredCandidates = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return candidates.filter((candidate) => {
      if (term) {
        const campos = [
          candidate.fullName,
          candidate.email,
          candidate.phone,
          candidate.jobRequest?.areaVacante,
          candidate.jobRequest?.puestoSolicitar,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!campos.includes(term)) return false;
      }

      if (filtroArea && candidate.jobRequest?.areaVacante !== filtroArea) return false;
      if (filtroPuesto && candidate.jobRequest?.puestoSolicitar !== filtroPuesto) return false;
      if (filtroSede && candidate.jobRequest?.sede !== filtroSede) return false;

      if (desde || hasta) {
        const dia = formatFechaEnZona(candidate.createdAt);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dia)) return false;
        if (desde && dia < desde) return false;
        if (hasta && dia > hasta) return false;
      }

      return true;
    });
  }, [candidates, searchTerm, filtroArea, filtroPuesto, filtroSede, desde, hasta]);

  const hayFiltros =
    Boolean(searchTerm || filtroArea || filtroPuesto || filtroSede || desde || hasta);

  const limpiarFiltros = () => {
    setSearchTerm("");
    setFiltroArea("");
    setFiltroPuesto("");
    setFiltroSede("");
    setDesde("");
    setHasta("");
  };

  const handleHireClick = (candidateId: string) => {
    navigate(`/hiring/hire/${candidateId}`);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
          <EsqueletoTabla filas={7} columnas={7} />
        </div>
      </div>
    );
  }

  if (
    userHasPermission(
      "rh.empleados_preregistro.base.read",
      user?.rbac?.effectivePermissions || []
    ) === false
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 mt-3">
      <div className="w-full max-w-none">
        {/* Header. El total va como texto a la derecha: era una tarjeta de 315px
            que ocupaba una franja entera para decir un número. */}
        {/* items-end: el conteo va a la altura de la descripción, no del título. */}
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <TituloDePantalla className="mb-2">
              Preregistro de candidatos
            </TituloDePantalla>
            <p className="text-[#1E1E1F] text-sm">
              Candidatos listos para el proceso de contratación
            </p>
          </div>
          <p className="flex items-center gap-2.5 text-sm text-gray-600 whitespace-nowrap">
            Candidatos listos
            {/* tabular-nums para que la pastilla no cambie de ancho al filtrar. */}
            <Badge color="morado" className="tabular-nums">
              {filteredCandidates.length.toLocaleString('es-MX')}
            </Badge>
          </p>
        </div>

        {/* Barra de filtros: la misma de almacén —fila blanca, buscador con
            lupa, FilterSelect en vez de select nativo y Limpiar como texto. */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap justify-start items-center gap-3 mb-6">
          <div className="relative w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar candidato..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998]"
            />
          </div>

          <FilterSelect
            label="Área"
            options={areas.map((o) => ({ value: o, label: o }))}
            value={filtroArea}
            onChange={setFiltroArea}
            ancho="contenido"
          />
          <FilterSelect
            label="Puesto"
            options={puestos.map((o) => ({ value: o, label: o }))}
            value={filtroPuesto}
            onChange={setFiltroPuesto}
            ancho="contenido"
          />
          <FilterSelect
            label="Sede"
            options={sedes.map((o) => ({ value: o, label: o }))}
            value={filtroSede}
            onChange={setFiltroSede}
            ancho="contenido"
          />

          <input
            type="date"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
            aria-label="Registrados desde"
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#3B5998] w-[160px] date-brand"
            style={{ colorScheme: 'light' }}
          />
          <input
            type="date"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
            aria-label="Registrados hasta"
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#3B5998] w-[160px] date-brand"
            style={{ colorScheme: 'light' }}
          />

          {hayFiltros && (
            <button
              onClick={limpiarFiltros}
              className="text-sm font-semibold text-[#315FB9] hover:underline"
            >
              Limpiar
            </button>
          )}
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-4 mb-6 shadow-sm">
            <p className="text-red-700 font-semibold">{error}</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredCandidates.length === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No hay candidatos listos para contratar
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {hayFiltros
                ? "Ningún candidato coincide con los filtros aplicados"
                : "No hay candidatos en estado SELECCIONADO en este momento"}
            </p>
          </div>
        )}

        {/* Candidates Table */}
        {filteredCandidates.length > 0 && (
          <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-[#E5E7EB]">
                <thead className="bg-[#FAF9FF]">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider"
                    >
                      Candidato
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider"
                    >
                      Correo
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider"
                    >
                      Teléfono
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider"
                    >
                      Área
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider"
                    >
                      Puesto
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider"
                    >
                      Estado
                    </th>
                    {userHasPermission(
                      "rh.empleados_preregistro.base.update",
                      user?.rbac?.effectivePermissions || []
                    ) && (
                      <th
                        scope="col"
                        className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider"
                      >
                        Acciones
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-[#E1E1E1]">
                  {filteredCandidates.map((candidate, _index) => (
                    <tr
                      key={candidate.id}
                      className="hover:bg-[#F4F7FB] transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm text-[#1B2128]">
                            {candidate.fullName || ""}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-[#1B2128]">
                          {candidate.email || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {candidate.phone || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-[#1B2128]">
                          {candidate.jobRequest?.areaVacante || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-[#1B2128]">
                          {candidate.jobRequest?.puestoSolicitar || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge color="verde">
                          {candidate.status == 'seleccionado' ? 'Seleccionado' : ""}
                        </Badge>
                      </td>
                      {userHasPermission(
                        "rh.empleados_preregistro.base.update",
                        user?.rbac?.effectivePermissions || []
                      ) && (
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleHireClick(candidate.id)}
                            className="inline-flex items-center gap-2 text-sm font-medium text-[#315FB9] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#315FB9]/40 rounded"
                          >
                            <UserPlus className="w-4 h-4" />
                            Empezar registro
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Permission Warning */}
        {!userHasPermission(
          "rh.empleados_preregistro.base.update",
          user?.rbac?.effectivePermissions || []
        ) &&
          candidates.length > 0 && (
            <div className="mt-6 bg-orange-50 border-l-4 border-orange-500 rounded-r-lg p-4 shadow-sm">
              <p className="text-orange-700 text-sm font-semibold leading-relaxed">
                ⚠️ No tienes permisos para contratar candidatos. Contacta al
                administrador para obtener acceso.
              </p>
            </div>
          )}
      </div>
    </div>
  );
};
